import React from "react";
import { useLocation } from "react-router-dom";
import SettingsReducer from "./reducers/Settings";
import AuthReducer from "./reducers/Auth";
import NotesReducer from "./reducers/Notes";
import ContactReducer from "./reducers/Contact";
import CommonReducer from "./reducers/Common";
import UsersReducer from "./reducers/Users";
import {
  FETCH_ALL_NOTES_SUCCESS,
  NOTES_UPDATE,
  ON_HIDE_LOADER,
  SIGNIN_USER,
  SIGNIN_USER_SUCCESS,
  SIGNOUT_USER,
  SIGNOUT_USER_SUCCESS,
  SIGNUP_USER,
  SIGNUP_USER_SUCCESS,
  SHOW_MESSAGE,
  UPDATE_ALL_NOTES_SUCCESS,
} from "constants/ActionTypes";
import { post } from "util/HttpRequest";
import { errorMessage, setUser } from "util/DataUser";

const AppStateContext = React.createContext(undefined);
const AppDispatchContext = React.createContext(undefined);

const NOTES_STORAGE_KEY = "sticky-notes";

const rootReducer = (state, action) => ({
  settings: SettingsReducer(state.settings, action),
  auth: AuthReducer(state.auth, action),
  notes: NotesReducer(state.notes, action),
  contact: ContactReducer(state.contact, action),
  common: CommonReducer(state.common, action),
  users: UsersReducer(state.users, action),
});

const initState = rootReducer(
  {
    settings: undefined,
    auth: undefined,
    notes: undefined,
    contact: undefined,
    common: undefined,
    users: undefined,
  },
  { type: "@@INIT" }
);

export const Provider = ({ children }) => {
  const location = useLocation();
  const [state, setState] = React.useState(initState);
  const stateRef = React.useRef(state);

  React.useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const baseDispatch = React.useCallback((action) => {
    setState((prev) => rootReducer(prev, action));
    return action;
  }, []);

  React.useEffect(() => {
    baseDispatch({
      type: "@@router/LOCATION_CHANGE",
      payload: { location },
    });
  }, [location.pathname, baseDispatch, location]);

  const dispatch = React.useCallback(
    async (action) => {
      if (typeof action === "function") {
        return action(dispatch, () => stateRef.current);
      }

      if (!action || typeof action.type !== "string") {
        return action;
      }

      if (action.type === SIGNIN_USER) {
        try {
          const { email, password } = action.payload || {};
          const response = await post({
            url: "/auth/login",
            data: { username: email, password },
          });
          if (!response?.data?.token) {
            baseDispatch({
              type: SHOW_MESSAGE,
              payload: response?.data?.message || "Login failed",
            });
          } else {
            localStorage.setItem("secret", response.data.token);
            localStorage.setItem("token", JSON.stringify(response.data.data));
            setUser(response.data.data);
            baseDispatch({ type: SIGNIN_USER_SUCCESS, payload: response.data.data });
          }
        } catch (e) {
          baseDispatch({ type: SHOW_MESSAGE, payload: errorMessage(e) });
        } finally {
          baseDispatch({ type: ON_HIDE_LOADER });
        }
        return;
      }

      if (action.type === SIGNUP_USER) {
        try {
          const { email, password } = action.payload || {};
          const response = await post({
            url: "/auth/register",
            data: { username: email, password },
          });
          const userData = response?.data?.data;
          if (!userData) {
            baseDispatch({
              type: SHOW_MESSAGE,
              payload: response?.data?.message || "Sign up failed",
            });
          } else {
            localStorage.setItem("user_id", userData.id || userData.user_id || "");
            baseDispatch({ type: SIGNUP_USER_SUCCESS, payload: userData });
          }
        } catch (e) {
          baseDispatch({ type: SHOW_MESSAGE, payload: errorMessage(e) });
        } finally {
          baseDispatch({ type: ON_HIDE_LOADER });
        }
        return;
      }

      if (action.type === SIGNOUT_USER) {
        localStorage.clear();
        setUser({});
        baseDispatch({ type: SIGNOUT_USER_SUCCESS });
        window.location.reload();
        return;
      }

      if (action.type === NOTES_UPDATE) {
        const nextNotes = action.notesList || [];
        localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(nextNotes));
        baseDispatch({ type: UPDATE_ALL_NOTES_SUCCESS, payload: nextNotes });
        return;
      }

      if (action.type === "GET_STICKY") {
        const notes = JSON.parse(localStorage.getItem(NOTES_STORAGE_KEY) || "[]");
        baseDispatch({ type: FETCH_ALL_NOTES_SUCCESS, payload: notes });
        return;
      }

      return baseDispatch(action);
    },
    [baseDispatch]
  );

  return (
    <AppStateContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>{children}</AppDispatchContext.Provider>
    </AppStateContext.Provider>
  );
};

export const useSelector = (selector) => {
  const state = React.useContext(AppStateContext);
  if (state === undefined) {
    throw new Error("useSelector must be used within Provider");
  }
  return selector(state);
};

export const useDispatch = () => {
  const dispatch = React.useContext(AppDispatchContext);
  if (dispatch === undefined) {
    throw new Error("useDispatch must be used within Provider");
  }
  return dispatch;
};

export const connect = (mapStateToProps, mapDispatchToProps) => (WrappedComponent) => {
  const Connected = (props) => {
    const state = useSelector((s) => s);
    const dispatch = useDispatch();
    const stateProps = mapStateToProps ? mapStateToProps(state, props) : {};
    let dispatchProps = { dispatch };

    if (typeof mapDispatchToProps === "function") {
      dispatchProps = mapDispatchToProps(dispatch, props);
    } else if (mapDispatchToProps && typeof mapDispatchToProps === "object") {
      dispatchProps = Object.keys(mapDispatchToProps).reduce((acc, key) => {
        acc[key] = (...args) => dispatch(mapDispatchToProps[key](...args));
        return acc;
      }, {});
    }

    return <WrappedComponent {...props} {...stateProps} {...dispatchProps} />;
  };

  return Connected;
};
